import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { debounce } from 'lodash';
import { validPassword } from 'ciam-self-service-shared';
import { validatePassword } from 'utils/validatePassword';

/**
 * Custom hook for password validation with UI and API validation
 * @param {Object} options - Configuration options
 * @param {Object} options.error - Current error object from Redux/state
 * @param {Function} options.clearError - Function to clear errors
 * @param {boolean} [options.isSignupFlow=false] - Whether this is used in signup flow (vs password reset flow)
 * @param {string} [options.firstName=''] - User's first name (optional)
 * @param {string} [options.lastName=''] - User's last name (optional)
 * @param {string} [options.username=''] - Username (optional)
 * @returns {Object} - Password validation utilities and state
 */
const usePasswordValidation = ({
  error,
  clearError,
  isSignupFlow = false,
  firstName = '',
  lastName = '',
  username = '',
}) => {
  const [passwordValidationError, setPasswordValidationError] = useState([]);
  const validationVersionRef = useRef(0);

  const performApiPasswordValidation = useCallback(async (password) => {
    validationVersionRef.current += 1;
    const currentVersion = validationVersionRef.current;

    try {
      const validationResult = await validatePassword(password);

      if (currentVersion !== validationVersionRef.current) {
        return { isValid: true, errors: [], stale: true };
      }

      if (validationResult.isValid) {
        setPasswordValidationError([]);
        return { isValid: true, errors: [] };
      }

      const errors = validationResult.error?.passwordValidationError || [];
      setPasswordValidationError(errors);
      // Treat as valid and proceed if errors come as null
      return { isValid: errors?.length === 0, errors };
    } catch (validationError) {
      if (currentVersion === validationVersionRef.current) {
        // Continue if there are any unexpected errors
        // Password validation will be invoked again at the ForgeRock side
        setPasswordValidationError([]);
      }
      return { isValid: true, errors: [] };
    }
  }, []);

  const debounceAdditionalPasswordValidations = useMemo(() => {
    return debounce(async (password) => {
      const validationResult = await performApiPasswordValidation(password);
      return validationResult.isValid;
    }, 300); // 300ms debounce - waits for user to stop typing
  }, [performApiPasswordValidation]);

  const performPasswordValidation = useCallback(
    (value) => {
      // For signup flow, perform UI validation against firstName/lastName/username
      // For reset flow, skip UI validation (handled by PasswordInput component)
      if (isSignupFlow) {
        const { error: uiValidationError } = validPassword({ firstName, lastName, username }).validate(value);

        if (uiValidationError) {
          // Clear API validation errors and cancel any pending debounced calls
          // Don't call API validation when UI validation fails
          setPasswordValidationError([]);
          debounceAdditionalPasswordValidations.cancel();
          return;
        }
      }

      // Only call API validation if UI validation passes (or reset flow)
      debounceAdditionalPasswordValidations(value);
    },
    [isSignupFlow, firstName, lastName, username, debounceAdditionalPasswordValidations],
  );

  // Combine errors from multiple sources
  // API validation errors are only present when UI validation passes
  const passwordErrors = useMemo(() => {
    const errors = [];
    if (error) {
      errors.push(error);
    }

    if (passwordValidationError?.length) {
      errors.push(...passwordValidationError);
    }

    return errors;
  }, [error, passwordValidationError]);

  const handlePasswordChange = useCallback(
    (value, setPassword) => {
      setPassword(value);
      if (error?.component === 'password') {
        clearError();
      }
      setPasswordValidationError([]);
      performPasswordValidation(value);
    },
    [error?.component, clearError, performPasswordValidation],
  );

  useEffect(() => {
    return () => {
      debounceAdditionalPasswordValidations.cancel();
    };
  }, [debounceAdditionalPasswordValidations]);

  return {
    passwordValidationError,
    passwordErrors,
    performPasswordValidation,
    handlePasswordChange,
  };
};

export default usePasswordValidation;
